import type { ToolCallApprovalPayload } from "@/components/assistant/findActiveWriteApproval";
import { mergePendingWriteApprovalMaps } from "@/components/assistant/mergePendingWriteApprovalMaps";
import { getJwtToken } from "@/lib/auth-client";
import type { Dispatch, SetStateAction } from "react";

export type FetchAndMergePendingWriteApprovalsArgs = {
  pendingUrl: string;
  isWriteToolName: (toolName: string) => boolean;
  normalizeProposalInput: (raw: unknown) => Record<string, unknown>;
  setApprovalByToolCallId: Dispatch<
    SetStateAction<Record<string, ToolCallApprovalPayload>>
  >;
  resolvedToolCallIds?: ReadonlySet<string>;
};

export async function fetchAndMergePendingWriteApprovals({
  pendingUrl,
  isWriteToolName,
  normalizeProposalInput,
  setApprovalByToolCallId,
  resolvedToolCallIds,
}: FetchAndMergePendingWriteApprovalsArgs): Promise<
  Record<string, ToolCallApprovalPayload>
> {
  const token = await getJwtToken();
  const res = await fetch(pendingUrl, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) return {};
  const body = (await res.json()) as {
    runs?: Array<{
      runId: string;
      suspendPayload?: { toolCallId?: string; toolName?: string; args?: unknown };
    }>;
  };
  const next: Record<string, ToolCallApprovalPayload> = {};
  for (const run of body.runs ?? []) {
    const toolCallId = run.suspendPayload?.toolCallId;
    const toolName = run.suspendPayload?.toolName;
    if (!toolCallId || !toolName || !isWriteToolName(toolName)) continue;
    if (resolvedToolCallIds?.has(toolCallId)) continue;
    next[toolCallId] = {
      runId: run.runId,
      toolCallId,
      toolName,
      args: normalizeProposalInput(run.suspendPayload?.args),
    };
  }
  setApprovalByToolCallId((prev) =>
    mergePendingWriteApprovalMaps(prev, next, resolvedToolCallIds)
  );
  return next;
}

export async function pollPendingWriteApprovalsUntilReady({
  pendingUrl,
  toolCallId,
  isWriteToolName,
  normalizeProposalInput,
  setApprovalByToolCallId,
  resolvedToolCallIds,
  intervalMs = 300,
  maxAttempts = 10,
}: FetchAndMergePendingWriteApprovalsArgs & {
  toolCallId: string;
  intervalMs?: number;
  maxAttempts?: number;
}): Promise<void> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const next = await fetchAndMergePendingWriteApprovals({
      pendingUrl,
      isWriteToolName,
      normalizeProposalInput,
      setApprovalByToolCallId,
      resolvedToolCallIds,
    });
    if (next[toolCallId]?.runId) return;
    if (attempt < maxAttempts - 1) {
      await new Promise((r) => setTimeout(r, intervalMs));
    }
  }
}
